// =============================================================================
// Example block data for few-shot prompts (complete tier)
// =============================================================================
// These examples guide Claude to produce well-formed blocks at each
// progression zone. Each example is a condensed but realistic block JSON.
// Only the most common block types have examples; others rely on the
// schema and instructions alone.
// =============================================================================

import type { ProgressionZone } from './level-progression.ts';

/**
 * Each example is keyed by `{zone}:{blockType}` and stores a JSON string
 * ready to be inserted into a prompt.
 */
export const EXAMPLE_BLOCKS: Record<string, string> = {
  // ---------------------------------------------------------------------------
  // Foundation — A1 level, basic vocabulary and simple structures
  // ---------------------------------------------------------------------------

  'foundation:vocabulary_grid': JSON.stringify(
    {
      id: '00000000-0000-0000-0000-000000000001',
      type: 'vocabulary_grid',
      order: 2,
      title: 'En el Aeropuerto',
      titleNative: 'At the Airport',
      theme: 'At the airport',
      estimatedPages: 1.5,
      instructions: 'Fill in the missing words in either Spanish or English.',
      instructionsNative: 'Completa las palabras que faltan en español o en inglés.',
      items: [
        { target: 'el pasaporte', native: 'the passport', hideTarget: false, hideNative: true },
        { target: 'la maleta', native: 'the suitcase', hideTarget: false, hideNative: true },
        { target: '___', native: 'the boarding pass', hideTarget: true, hideNative: false },
        { target: 'la salida', native: 'the exit', hideTarget: false, hideNative: true },
        { target: '___', native: 'the gate', hideTarget: true, hideNative: false },
        { target: 'el vuelo', native: 'the flight', hideTarget: false, hideNative: false },
        { target: '___', native: 'the arrival', hideTarget: true, hideNative: false },
        { target: 'la aduana', native: 'customs', hideTarget: false, hideNative: true },
        { target: 'el equipaje', native: '___', hideTarget: false, hideNative: true },
        { target: '___', native: 'the seat', hideTarget: true, hideNative: false },
      ],
    },
    null,
    2,
  ),

  'foundation:guided_conversation': JSON.stringify(
    {
      id: '00000000-0000-0000-0000-000000000002',
      type: 'guided_conversation',
      order: 4,
      title: 'En la Recepción del Hotel',
      titleNative: 'At the Hotel Reception',
      theme: 'Hotel check-in',
      estimatedPages: 2,
      situation: {
        description: 'You are checking into a small hotel in Madrid. The receptionist greets you.',
        descriptionNative:
          'Estás registrándote en un pequeño hotel en Madrid. El recepcionista te saluda.',
        emoji: '🏨',
      },
      turns: [
        {
          number: 1,
          role: 'other',
          roleName: 'Receptionist',
          lineTarget: '¡Buenas tardes! Bienvenido al Hotel Sol. ¿Tiene una reserva?',
          lineNative: 'Good afternoon! Welcome to Hotel Sol. Do you have a reservation?',
          isPrompt: false,
        },
        {
          number: 2,
          role: 'learner',
          roleName: 'You',
          lineTarget: 'Sí, tengo una reserva a nombre de...',
          lineNative: 'Yes, I have a reservation under the name of...',
          isPrompt: true,
          starterHint: 'Sí, tengo...',
        },
        {
          number: 3,
          role: 'other',
          roleName: 'Receptionist',
          lineTarget: 'Perfecto. Su habitación es la número 204. Aquí tiene la llave.',
          lineNative: 'Perfect. Your room is number 204. Here is the key.',
          isPrompt: false,
        },
        {
          number: 4,
          role: 'learner',
          roleName: 'You',
          lineTarget: 'Gracias. ¿A qué hora es el desayuno?',
          lineNative: 'Thank you. What time is breakfast?',
          isPrompt: true,
          starterHint: 'Gracias. ¿A qué hora...',
        },
        {
          number: 5,
          role: 'other',
          roleName: 'Receptionist',
          lineTarget: 'El desayuno es de siete a diez de la mañana en el restaurante.',
          lineNative: 'Breakfast is from seven to ten in the morning in the restaurant.',
          isPrompt: false,
        },
        {
          number: 6,
          role: 'learner',
          roleName: 'You',
          lineTarget: '¿Hay wifi en la habitación?',
          lineNative: 'Is there wifi in the room?',
          isPrompt: true,
          starterHint: '¿Hay wifi...',
        },
        {
          number: 7,
          role: 'other',
          roleName: 'Receptionist',
          lineTarget: 'Sí, la contraseña está en la tarjeta de la habitación.',
          lineNative: 'Yes, the password is on the room card.',
          isPrompt: false,
        },
        {
          number: 8,
          role: 'learner',
          roleName: 'You',
          lineTarget: 'Muchas gracias. Buenas tardes.',
          lineNative: 'Thank you very much. Good afternoon.',
          isPrompt: true,
          starterHint: 'Muchas gracias...',
        },
      ],
      vocabularyItems: [
        { target: 'la reserva', native: 'the reservation' },
        { target: 'la habitación', native: 'the room' },
        { target: 'la llave', native: 'the key' },
        { target: 'el desayuno', native: 'breakfast' },
      ],
      tip: {
        emoji: '💡',
        text: 'In Spanish hotels, "planta baja" means ground floor — room 204 is on the second floor!',
      },
    },
    null,
    2,
  ),

  'foundation:reading_comprehension': JSON.stringify(
    {
      id: '00000000-0000-0000-0000-000000000003',
      type: 'reading_comprehension',
      order: 6,
      title: 'Un Correo del Hotel',
      titleNative: 'An Email from the Hotel',
      theme: 'Hotel check-in',
      estimatedPages: 2,
      text: 'Estimado huésped,\n\nGracias por su reserva en el Hotel Sol. Su habitación número 204 está lista. El desayuno es de 7:00 a 10:00 en el restaurante del primer piso. La contraseña del wifi es "sol2024". El check-out es a las 12:00.\n\nSi necesita algo, llame a recepción al número 0.\n\n¡Bienvenido!\nEl equipo del Hotel Sol',
      textType: 'email',
      questions: [
        {
          question: '¿Cuál es el número de la habitación?',
          questionNative: 'What is the room number?',
          answer: '204',
          type: 'open',
        },
        {
          question: '¿El desayuno empieza a las 8:00?',
          questionNative: 'Does breakfast start at 8:00?',
          answer: 'False — breakfast starts at 7:00',
          type: 'true_false',
        },
        {
          question: '¿Cuál es la contraseña del wifi?',
          questionNative: 'What is the wifi password?',
          answer: 'sol2024',
          type: 'multiple_choice',
          options: ['hotel204', 'sol2024', 'bienvenido', 'recepcion0'],
        },
        {
          question: '¿A qué hora es el check-out?',
          questionNative: 'What time is check-out?',
          answer: 'At 12:00 (noon)',
          type: 'open',
        },
      ],
    },
    null,
    2,
  ),

  // ---------------------------------------------------------------------------
  // Transition — mixing A1 and A2, more complex structures
  // ---------------------------------------------------------------------------

  'transition:vocabulary_grid': JSON.stringify(
    {
      id: '00000000-0000-0000-0000-000000000004',
      type: 'vocabulary_grid',
      order: 35,
      title: 'Describir Experiencias de Viaje',
      titleNative: 'Describing Travel Experiences',
      theme: 'Tourist information',
      estimatedPages: 1.5,
      instructions: 'Complete the grid. Some entries require phrases, not single words.',
      instructionsNative: 'Completa la tabla. Algunas entradas requieren frases, no solo palabras.',
      items: [
        { target: 'hacer una excursión', native: '___', hideTarget: false, hideNative: true },
        { target: '___', native: 'to get lost', hideTarget: true, hideNative: false },
        {
          target: 'el recorrido guiado',
          native: 'the guided tour',
          hideTarget: false,
          hideNative: false,
        },
        { target: '___', native: 'to recommend', hideTarget: true, hideNative: false },
        { target: 'vale la pena', native: '___', hideTarget: false, hideNative: true },
        { target: 'el horario de apertura', native: '___', hideTarget: false, hideNative: true },
        { target: '___', native: 'to book in advance', hideTarget: true, hideNative: false },
        {
          target: 'la entrada gratuita',
          native: 'free admission',
          hideTarget: false,
          hideNative: false,
        },
        { target: 'sacar fotos', native: '___', hideTarget: false, hideNative: true },
        { target: '___', native: 'the souvenir shop', hideTarget: true, hideNative: false },
      ],
    },
    null,
    2,
  ),

  'transition:guided_conversation': JSON.stringify(
    {
      id: '00000000-0000-0000-0000-000000000005',
      type: 'guided_conversation',
      order: 38,
      title: 'Pidiendo Información Turística',
      titleNative: 'Asking for Tourist Information',
      theme: 'Tourist information',
      estimatedPages: 2,
      situation: {
        description:
          'You visit a tourist information office to ask about local attractions and get advice.',
        descriptionNative:
          'Visitas una oficina de información turística para preguntar sobre atracciones locales y pedir consejo.',
        emoji: '🗺️',
      },
      turns: [
        {
          number: 1,
          role: 'other',
          roleName: 'Agent',
          lineTarget: '¡Hola! ¿En qué puedo ayudarle?',
          lineNative: 'Hello! How can I help you?',
          isPrompt: false,
        },
        {
          number: 2,
          role: 'learner',
          roleName: 'You',
          lineTarget:
            'Buenos días. ¿Podría recomendarme algunos lugares interesantes para visitar?',
          lineNative: 'Good morning. Could you recommend some interesting places to visit?',
          isPrompt: true,
          starterHint: 'Buenos días. ¿Podría...',
        },
        {
          number: 3,
          role: 'other',
          roleName: 'Agent',
          lineTarget:
            'Por supuesto. ¿Le interesan más los museos o prefiere actividades al aire libre?',
          lineNative:
            'Of course. Are you more interested in museums or do you prefer outdoor activities?',
          isPrompt: false,
        },
        {
          number: 4,
          role: 'learner',
          roleName: 'You',
          lineTarget:
            'Me gustan las dos cosas, pero hoy prefiero algo al aire libre porque hace buen tiempo.',
          lineNative:
            'I like both, but today I prefer something outdoors because the weather is nice.',
          isPrompt: true,
          starterHint: 'Me gustan las dos cosas, pero hoy...',
        },
        {
          number: 5,
          role: 'other',
          roleName: 'Agent',
          lineTarget:
            'Entonces le recomiendo el Parque del Retiro. Puede alquilar una barca en el lago. También hay un recorrido guiado a las once.',
          lineNative:
            'Then I recommend Retiro Park. You can rent a boat on the lake. There is also a guided tour at eleven.',
          isPrompt: false,
        },
        {
          number: 6,
          role: 'learner',
          roleName: 'You',
          lineTarget: '¡Qué buena idea! ¿Cuánto cuesta el recorrido guiado?',
          lineNative: 'What a great idea! How much does the guided tour cost?',
          isPrompt: true,
          starterHint: '¡Qué buena idea! ¿Cuánto cuesta...',
        },
        {
          number: 7,
          role: 'other',
          roleName: 'Agent',
          lineTarget:
            'Es gratuito. Solo tiene que llegar a la entrada principal antes de las once.',
          lineNative: 'It is free. You just need to arrive at the main entrance before eleven.',
          isPrompt: false,
        },
        {
          number: 8,
          role: 'learner',
          roleName: 'You',
          lineTarget: 'Perfecto. ¿Tiene un mapa del parque?',
          lineNative: 'Perfect. Do you have a map of the park?',
          isPrompt: true,
          starterHint: 'Perfecto. ¿Tiene...',
        },
      ],
      vocabularyItems: [
        { target: 'recomendar', native: 'to recommend' },
        { target: 'al aire libre', native: 'outdoors' },
        { target: 'alquilar', native: 'to rent' },
        { target: 'gratuito', native: 'free (of charge)' },
        { target: 'la entrada principal', native: 'the main entrance' },
      ],
    },
    null,
    2,
  ),

  // ---------------------------------------------------------------------------
  // Arrival — A2 level, more confident and complex output
  // ---------------------------------------------------------------------------

  'arrival:vocabulary_grid': JSON.stringify(
    {
      id: '00000000-0000-0000-0000-000000000006',
      type: 'vocabulary_grid',
      order: 52,
      title: 'Expresiones para Opinar y Debatir',
      titleNative: 'Expressions for Giving Opinions and Debating',
      theme: 'Group activities',
      estimatedPages: 1.5,
      instructions:
        'Fill in the missing expressions. Pay attention to formal vs. informal register.',
      instructionsNative:
        'Completa las expresiones que faltan. Presta atención al registro formal e informal.',
      items: [
        { target: 'en mi opinión', native: '___', hideTarget: false, hideNative: true },
        { target: '___', native: 'I agree with you', hideTarget: true, hideNative: false },
        {
          target: 'no estoy de acuerdo porque...',
          native: 'I disagree because...',
          hideTarget: false,
          hideNative: false,
        },
        { target: '___', native: 'on the other hand', hideTarget: true, hideNative: false },
        { target: 'me parece que...', native: '___', hideTarget: false, hideNative: true },
        { target: 'tienes razón, pero...', native: '___', hideTarget: false, hideNative: true },
        { target: '___', native: 'for example', hideTarget: true, hideNative: false },
        { target: 'lo más importante es...', native: '___', hideTarget: false, hideNative: true },
        { target: '___', native: 'in conclusion', hideTarget: true, hideNative: false },
        {
          target: '¿qué opinas tú?',
          native: 'what do you think?',
          hideTarget: false,
          hideNative: false,
        },
      ],
    },
    null,
    2,
  ),

  'arrival:reading_comprehension': JSON.stringify(
    {
      id: '00000000-0000-0000-0000-000000000007',
      type: 'reading_comprehension',
      order: 55,
      title: 'Un Blog sobre Festivales Locales',
      titleNative: 'A Blog about Local Festivals',
      theme: 'Local festivals',
      estimatedPages: 2,
      text: 'El fin de semana pasado fui al Festival de la Tomatina en Buñol, un pueblo cerca de Valencia. Es una fiesta muy famosa donde la gente se lanza tomates durante una hora. Llegué temprano por la mañana y ya había miles de personas en las calles.\n\nAntes de la batalla de tomates, hubo música en vivo y bailes tradicionales. A las once, un camión grande trajo toneladas de tomates maduros. Cuando sonó la señal, todo el mundo empezó a lanzar tomates. ¡Fue increíble y muy divertido!\n\nDespués del festival, los bomberos limpiaron las calles con mangueras. Me duché en una zona habilitada y fui a comer una paella valenciana en un restaurante cercano. Sin duda, es una experiencia que recomiendo a todos.',
      textType: 'blog post',
      questions: [
        {
          question: '¿Dónde se celebra la Tomatina?',
          questionNative: 'Where is La Tomatina held?',
          answer: 'En Buñol, un pueblo cerca de Valencia.',
          type: 'open',
        },
        {
          question: '¿La batalla de tomates duró dos horas?',
          questionNative: 'Did the tomato battle last two hours?',
          answer: 'False — it lasted one hour (una hora).',
          type: 'true_false',
        },
        {
          question: '¿Qué pasó antes de la batalla?',
          questionNative: 'What happened before the battle?',
          answer: 'There was live music and traditional dances.',
          type: 'multiple_choice',
          options: [
            'Hubo un desfile de carrozas',
            'Hubo música en vivo y bailes tradicionales',
            'Sirvieron paella a todos',
            'Los bomberos prepararon las calles',
          ],
        },
        {
          question: '¿Qué hizo el autor después del festival?',
          questionNative: 'What did the author do after the festival?',
          answer: 'Se duchó y fue a comer paella valenciana.',
          type: 'open',
        },
        {
          question: '¿El autor recomienda la experiencia?',
          questionNative: 'Does the author recommend the experience?',
          answer: 'Yes — "es una experiencia que recomiendo a todos".',
          type: 'true_false',
        },
      ],
    },
    null,
    2,
  ),
};

/**
 * Returns an example block JSON string for a given zone and block type,
 * or `null` if no example is available for that combination.
 */
export function getExampleBlock(zone: ProgressionZone, blockType: string): string | null {
  return EXAMPLE_BLOCKS[`${zone}:${blockType}`] ?? null;
}
